import { CommentsSettings } from "@/components/CommentsSettings";
import { useTurnHighlight } from '@/lib/useTurnHighlight';
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  siteKeys,
  sitesApi,
  openExternal,
  type PreviewShare,
  type PreviewShareCreate,
  useSite,
  useUpdateSite,
  useDeleteSite,
  useAccounts,
  useSiteSettings,
  useUpdateSiteSettings,
  useActivityPubStatus,
  useEnableActivityPub,
  useDisableActivityPub,
  useThemes,
  useAccountThemes,
  parseUpgradeRequired,
} from "@wordpuppi/shared";
import { UpgradeNotice } from "@/components/UpgradeNotice";
import { confirmDialog } from "@/lib/dialog";
import { isMemberOf } from "@/lib/activeAccount";
import { useBaxterChanged } from "@/lib/baxterEffects";
import { BaxterChangedBar } from "@/components/baxter/BaxterChangedBar";
import { copyText } from "@/lib/clipboard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export const Route = createFileRoute("/sites/$siteSlug/settings/")({
  component: GeneralSettingsPage,
});

function GeneralSettingsPage() {
  const { siteSlug } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useSite(siteSlug);
  const updateSite = useUpdateSite();
  const deleteSite = useDeleteSite();
  const { data: accountsData } = useAccounts();

  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");

  // This form had no dirty tracking, so every refetch overwrote whatever was
  // half-typed. Baseline vs current, same shape as the page/post editors.
  const [baseline, setBaseline] = useState<string | null>(null);
  const dirty = baseline !== null && baseline !== JSON.stringify({ name, domain });
  const flash = useTurnHighlight(data?.data ? [{ id: siteSlug, name: data.data.name, domain: data.data.domain }] : undefined, useBaxterChanged('settings'));

  const initialized = useRef(false);
  useEffect(() => {
    if (!data?.data) return;
    // The very first load reads "dirty" (empty fields vs the server's) — it isn't.
    if (initialized.current && dirty) return;
    setBaseline(JSON.stringify({ name: data.data.name, domain: data.data.domain || "" }));
    setName(data.data.name);
    setDomain(data.data.domain || "");
    initialized.current = true;
  }, [data]);

  // AB#675: Baxter renamed the site / changed settings under an open form.
  const baxterAt = useBaxterChanged("settings");
  const seenBaxter = useRef<number | null>(null);
  const [changedBar, setChangedBar] = useState(false);
  const reloadFromServer = async () => {
    const fresh = await refetch();
    const site = fresh.data?.data;
    if (site) {
      setBaseline(JSON.stringify({ name: site.name, domain: site.domain || "" }));
      setName(site.name);
      setDomain(site.domain || "");
    }
    setChangedBar(false);
  };
  useEffect(() => {
    if (baxterAt == null || baxterAt === seenBaxter.current) return;
    seenBaxter.current = baxterAt;
    // Clean: the effect above already re-copied off the invalidated query.
    if (dirty) setChangedBar(true);
  }, [baxterAt, dirty]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Site not found
      </div>
    );
  }

  const site = data.data;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateSite.mutateAsync({
        slug: siteSlug,
        data: { name, domain: domain || undefined },
      });
      setBaseline(JSON.stringify({ name, domain }));
      setChangedBar(false);
      toast.success("Site settings updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update site");
    }
  };

  const handleDelete = async () => {
    if (
      await confirmDialog(
        `Are you sure you want to delete "${site.name}"? This action cannot be undone.`,
        { destructive: true, confirmLabel: 'Delete site' },
      )
    ) {
      try {
        await deleteSite.mutateAsync(siteSlug);
        toast.success("Site deleted");
        navigate({ to: "/sites" });
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to delete site",
        );
      }
    }
  };

  return (
    <div className="space-y-6">
      {changedBar && (
        <BaxterChangedBar
          noun="site"
          onReload={() => void reloadFromServer()}
          onDismiss={() => setChangedBar(false)}
        />
      )}
      <Card className={!dirty && flash.has(siteSlug) ? "baxter-changed" : undefined}>
        <CardHeader>
          <CardTitle>Basic Settings</CardTitle>
          <CardDescription>
            The name, address, and storage for this site.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Site Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                type="text"
                value={site.slug}
                disabled
                className="bg-muted"
              />
              <p className="text-sm text-muted-foreground">
                Slug cannot be changed after creation
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dbType">Database</Label>
              <div className="flex gap-2">
                <Input
                  id="dbType"
                  type="text"
                  value={
                    site.db_type === "sqlite"
                      ? "Internal Built-in"
                      : site.db_type.toUpperCase()
                  }
                  disabled
                  className="bg-muted flex-1"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    navigate({
                      to: "/sites/$siteSlug/migrate",
                      params: { siteSlug },
                    })
                  }
                >
                  Migrate...
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Migrate your content to a different database
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="domain">Domain</Label>
              <Input
                id="domain"
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="blog.example.com"
              />
              <p className="text-sm text-muted-foreground">
                Set a custom domain for your site. If using Cloudflare Pages,
                this will be added as a custom domain on your next deploy.
              </p>
            </div>

            <Button type="submit" disabled={updateSite.isPending}>
              {updateSite.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <PreviewLinkSection siteSlug={siteSlug} previewUrl={site.preview_url} />

      <CommentsSettings key={siteSlug} siteSlug={siteSlug} />

      <ActivityPubSettingsSection siteSlug={siteSlug} />

      {/* AB#1016: deleting a site is owner-only. */}
      {!isMemberOf(accountsData?.data ?? [], site.account_pid) && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Deleting a site will permanently remove all its content, including
              posts, pages, and media.
            </p>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteSite.isPending}
            >
              {deleteSite.isPending ? "Deleting..." : "Delete Site"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActivityPub Settings Section
// ---------------------------------------------------------------------------

const AP_ENABLED = false;

function ActivityPubSettingsSection({ siteSlug }: { siteSlug: string }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: apStatus, isLoading } = useActivityPubStatus(siteSlug);
  const enableAP = useEnableActivityPub(siteSlug);
  const disableAP = useDisableActivityPub(siteSlug);
  const { data: settingsData } = useSiteSettings(siteSlug);
  const updateSettings = useUpdateSiteSettings(siteSlug);

  const [apUsername, setApUsername] = useState("");
  const [apDisplayName, setApDisplayName] = useState("");
  const [apSummary, setApSummary] = useState("");
  const [apAutoAccept, setApAutoAccept] = useState(true);
  const [apFullContent, setApFullContent] = useState(true);

  useEffect(() => {
    if (settingsData?.data) {
      const ap = settingsData.data.activitypub;
      if (ap) {
        setApUsername(ap.username || "");
        setApDisplayName(ap.display_name || "");
        setApSummary(ap.summary || "");
        setApAutoAccept(ap.auto_accept_follows ?? true);
        setApFullContent(ap.include_full_content ?? true);
      }
    }
  }, [settingsData]);

  const handleToggle = async (checked: boolean) => {
    try {
      if (checked) {
        await enableAP.mutateAsync();
        toast.success("ActivityPub enabled");
      } else {
        await disableAP.mutateAsync();
        toast.success("ActivityPub disabled");
      }
    } catch {
      toast.error("Failed to toggle ActivityPub");
    }
  };

  const handleSaveAPSettings = async () => {
    try {
      await updateSettings.mutateAsync({
        activitypub: {
          enabled: apStatus?.enabled ?? false,
          username: apUsername || undefined,
          display_name: apDisplayName || undefined,
          summary: apSummary || undefined,
          auto_accept_follows: apAutoAccept,
          include_full_content: apFullContent,
        },
      });
      toast.success("ActivityPub settings saved");
    } catch {
      toast.error("Failed to save ActivityPub settings");
    }
  };

  if (isLoading) return <Skeleton className="h-32 w-full" />;

  if (!AP_ENABLED) {
    return (
      <>
        <Card
          className="opacity-60 cursor-help select-none"
          onClick={() => setDialogOpen(true)}
        >
          <CardHeader>
            <CardTitle>Fediverse / ActivityPub</CardTitle>
            <CardDescription>
              Make your site followable on Mastodon and other fediverse
              platforms.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <Label>Enable ActivityPub</Label>
              <Switch checked={false} disabled />
            </div>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Fediverse / ActivityPub</DialogTitle>
              <DialogDescription>
                This feature is coming soon. If enabled, your site will become a
                followable actor on the fediverse. People on Mastodon, Pleroma,
                Misskey and other ActivityPub-compatible platforms will be able
                to follow your blog and see new posts directly in their
                timeline. Likes, boosts, and replies will flow back as
                engagement.
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fediverse / ActivityPub</CardTitle>
        <CardDescription>
          Make your site followable on Mastodon and other fediverse platforms.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Enable ActivityPub</Label>
          <Switch
            checked={apStatus?.enabled ?? false}
            onCheckedChange={handleToggle}
            disabled={enableAP.isPending || disableAP.isPending}
          />
        </div>

        {apStatus?.enabled && (
          <>
            {apStatus.username && (
              <p className="text-sm text-muted-foreground">
                Fediverse handle:{" "}
                <code>
                  @{apStatus.username}@{window.location.hostname}
                </code>
              </p>
            )}

            {apStatus.follower_count != null && (
              <p className="text-sm text-muted-foreground">
                {apStatus.follower_count} followers
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="ap-username">Username</Label>
              <Input
                id="ap-username"
                value={apUsername}
                onChange={(e) => setApUsername(e.target.value)}
                placeholder={siteSlug}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ap-display-name">Display Name</Label>
              <Input
                id="ap-display-name"
                value={apDisplayName}
                onChange={(e) => setApDisplayName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ap-summary">Summary</Label>
              <Textarea
                id="ap-summary"
                value={apSummary}
                onChange={(e) => setApSummary(e.target.value)}
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Auto-accept follows</Label>
              <Switch
                checked={apAutoAccept}
                onCheckedChange={setApAutoAccept}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Include full content in federation</Label>
              <p className="text-sm text-muted-foreground">Native federation uses the site default language ({settingsData?.data?.languages?.default || settingsData?.data?.language || 'en'}). Choose the default in Languages settings. Translation edits do not broadcast extra posts or change the actor identity.</p>
              <Switch
                checked={apFullContent}
                onCheckedChange={setApFullContent}
              />
            </div>

            <Button
              onClick={handleSaveAPSettings}
              disabled={updateSettings.isPending}
            >
              {updateSettings.isPending
                ? "Saving..."
                : "Save ActivityPub Settings"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

const SITE_THEME = "__site__";
const EXPIRY_OPTIONS: { value: string; label: string }[] = [
  { value: "1", label: "1 day" },
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "never", label: "Never" },
];

const shareExpiry = (s: PreviewShare) =>
  s.expires_at
    ? `Expires ${new Date(s.expires_at).toLocaleDateString()}`
    : "Never expires";

/**
 * AB#673: the site's own preview link plus the shares handed to other people.
 * Shares are their own query — small list, refetched on every mutation.
 */
function PreviewLinkSection({
  siteSlug,
  previewUrl,
}: {
  siteSlug: string;
  previewUrl?: string;
}) {
  const queryClient = useQueryClient();
  const shares = useQuery({
    queryKey: [...siteKeys.detail(siteSlug), "preview-shares"],
    queryFn: () => sitesApi.listPreviewShares(siteSlug),
  });
  const reload = () => {
    void shares.refetch();
  };

  const [mode, setMode] = useState<"public" | "email">("public");
  const [email, setEmail] = useState("");
  const [expiry, setExpiry] = useState("30");
  // AB#1061: SITE_THEME = the site's own theme; anything else is an Ultimate theme variant.
  // base-ui Select treats "" as no value and renders an empty trigger, hence the sentinel.
  const [variant, setVariant] = useState(SITE_THEME);
  const [label, setLabel] = useState("");
  const { data: bundledThemes } = useThemes();
  const { data: accountThemes } = useAccountThemes(siteSlug);
  const themeOptions = [
    ...(bundledThemes?.data ?? []).map((t) => ({ value: t.id, name: t.name })),
    ...(accountThemes?.data ?? []).map((t) => ({ value: String(t.id), name: t.name })),
  ];
  const themeName = (v: string) => themeOptions.find((o) => o.value === v)?.name ?? v;

  const rotate = useMutation({
    mutationFn: () => sitesApi.rotatePreviewKey(siteSlug),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteSlug) });
      toast.success("Preview link rotated");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Rotate failed"),
  });

  const create = useMutation({
    mutationFn: async (body: PreviewShareCreate) => {
      const res = await sitesApi.createPreviewShare(siteSlug, body);
      // Email shares are useless until they are mailed — do both in one click.
      const token = res.data?.token;
      if (body.mode === "email" && token) await sitesApi.sendPreviewShare(siteSlug, token);
      return body.mode;
    },
    onSuccess: (m) => {
      setEmail("");
      setLabel("");
      reload();
      toast.success(m === "email" ? "Created and sent" : "Share link created");
    },
    onError: (err: unknown) => {
      // A plan-gated variant shows the UpgradeNotice under the form instead.
      if (!parseUpgradeRequired(err))
        toast.error(err instanceof Error ? err.message : "Could not create the share");
    },
  });

  const copy = async (url: string) => {
    if (await copyText(url)) toast.success("Link copied");
  };

  const send = async (share: PreviewShare) => {
    try {
      await sitesApi.sendPreviewShare(siteSlug, share.token);
      toast.success(`Sent to ${share.email}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Send failed");
    }
  };

  const revoke = async (share: PreviewShare) => {
    if (!(await confirmDialog("Revoke this preview share? The link stops working immediately.", { destructive: true, confirmLabel: "Revoke share" })))
      return;
    try {
      await sitesApi.revokePreviewShare(siteSlug, share.token);
      reload();
      toast.success("Share revoked");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Revoke failed");
    }
  };

  const list = shares.data?.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Preview link</CardTitle>
        <CardDescription>
          A stable link to the last build — anyone with it can look, no login needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="previewUrl">Your link</Label>
          <div className="flex flex-wrap gap-2">
            <Input
              id="previewUrl"
              type="text"
              readOnly
              value={previewUrl ?? "Not available yet"}
              className="bg-muted flex-1 min-w-60"
            />
            <Button
              type="button"
              variant="secondary"
              disabled={!previewUrl}
              onClick={() => void copy(previewUrl!)}
            >
              Copy
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!previewUrl}
              onClick={() => void openExternal(previewUrl!)}
            >
              Open
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={rotate.isPending}
              onClick={async () => {
                if (
                  await confirmDialog(
                    "Rotating invalidates the current preview link for everyone who has it. Continue?",
                    { destructive: true, confirmLabel: "Rotate link" },
                  )
                )
                  rotate.mutate();
              }}
            >
              {rotate.isPending ? "Rotating..." : "Rotate"}
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <Label>Shares</Label>
          {list.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No shares yet — create one below to hand out a separate, revocable link.
            </p>
          ) : (
            <ul className="divide-y divide-border/60">
              {list.map((share) => (
                <li
                  key={share.token}
                  className="flex flex-wrap items-center gap-2 py-2"
                >
                  <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium">
                    {share.mode === "email" ? "Email" : "Public"}
                  </span>
                  {share.label && (
                    <span className="text-sm font-medium">{share.label}</span>
                  )}
                  <span className="text-sm">
                    {share.email ?? "Anyone with the link"}
                  </span>
                  {share.theme && (
                    <span className="rounded border px-2 py-0.5 text-xs text-muted-foreground">
                      {themeName(share.theme)} theme
                    </span>
                  )}
                  <span className="text-sm text-muted-foreground">
                    {shareExpiry(share)}
                  </span>
                  <div className="ml-auto flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => void copy(share.url)}
                    >
                      Copy
                    </Button>
                    {share.mode === "email" && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => void send(share)}
                      >
                        Send
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => void revoke(share)}
                    >
                      Revoke
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-end gap-2 rounded-lg border p-3">
            <div className="space-y-1.5">
              <Label>Kind</Label>
              <Select
                value={mode}
                onValueChange={(v: string) => setMode(v as "public" | "email")}
              >
                <SelectTrigger className="w-40">
                  {/* base-ui renders the RAW value without children. */}
                  <SelectValue>
                    {(v: unknown) => (v === "email" ? "Email" : "Public link")}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public link</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mode === "email" && (
              <div className="space-y-1.5">
                <Label htmlFor="shareEmail">Email</Label>
                <Input
                  id="shareEmail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="reviewer@example.com"
                  className="w-60"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Expires</Label>
              <Select value={expiry} onValueChange={setExpiry}>
                <SelectTrigger className="w-32">
                  <SelectValue>
                    {(v: unknown) =>
                      EXPIRY_OPTIONS.find((o) => o.value === v)?.label ?? ""
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {EXPIRY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="shareLabel">Label</Label>
              <Input
                id="shareLabel"
                value={label}
                maxLength={60}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Optional"
                className="w-44"
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                Theme <span className="font-normal text-muted-foreground">(Ultimate)</span>
              </Label>
              <Select
                value={variant}
                onValueChange={(v: string) => {
                  setVariant(v);
                  create.reset();
                }}
              >
                <SelectTrigger className="w-44">
                  <SelectValue>
                    {(v: unknown) => (v && v !== SITE_THEME ? themeName(String(v)) : "Site theme")}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SITE_THEME}>Site theme</SelectItem>
                  {themeOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              disabled={create.isPending || (mode === "email" && !email)}
              onClick={() =>
                create.mutate({
                  mode,
                  ...(mode === "email" ? { email } : {}),
                  ...(variant !== SITE_THEME ? { theme: variant } : {}),
                  ...(label.trim() ? { label: label.trim() } : {}),
                  expires_in_days:
                    expiry === "never"
                      ? null
                      : (Number(expiry) as PreviewShareCreate["expires_in_days"]),
                })
              }
            >
              {create.isPending ? "Creating..." : "Create"}
            </Button>
          </div>
          <UpgradeNotice error={create.error} />
        </div>
      </CardContent>
    </Card>
  );
}
